import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { tokenService } from '../services/axios'
import { useAuthStore } from '../stores/auth-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { permission: 'access.users' },
      },
      {
        name: 'user-create',
        path: 'users/create',
        component: () => import('../pages/users/UserCreate.vue'),
        meta: { permission: 'create.users' },
      },
      {
        name: 'user-edit',
        path: 'users/:id/edit',
        component: () => import('../pages/users/UserEdit.vue'),
        meta: { permission: 'update.users' },
      },
      {
        name: 'employees',
        path: 'employees',
        component: () => import('../pages/employees/EmployeesPage.vue'),
        meta: { permission: 'access.employees' },
      },
      {
        name: 'employee-create',
        path: 'employees/create',
        component: () => import('../pages/employees/EmployeeCreate.vue'),
        meta: { permission: 'create.employees' },
      },
      {
        name: 'employee-edit',
        path: 'employees/:id/edit',
        component: () => import('../pages/employees/EmployeeEdit.vue'),
        meta: { permission: 'update.employees' },
      },
      {
        name: 'suppliers',
        path: 'suppliers',
        component: () => import('../pages/suppliers/SuppliersPage.vue'),
        meta: { permission: 'access.suppliers' },
      },
      {
        name: 'supplier-create',
        path: 'suppliers/create',
        component: () => import('../pages/suppliers/SupplierCreate.vue'),
        meta: { permission: 'create.suppliers' },
      },
      {
        name: 'supplier-edit',
        path: 'suppliers/:id/edit',
        component: () => import('../pages/suppliers/SupplierEdit.vue'),
        meta: { permission: 'update.suppliers' },
      },
      {
        name: 'customers',
        path: 'customers',
        component: () => import('../pages/customers/CustomersPage.vue'),
        meta: { permission: 'access.customers' },
      },
      {
        name: 'customer-create',
        path: 'customers/create',
        component: () => import('../pages/customers/CustomerCreate.vue'),
        meta: { permission: 'create.customers' },
      },
      {
        name: 'customer-edit',
        path: 'customers/:id/edit',
        component: () => import('../pages/customers/CustomerEdit.vue'),
        meta: { permission: 'update.customers' },
      },
      {
        name: 'units',
        path: 'units',
        component: () => import('../pages/units/UnitsPage.vue'),
        meta: { permission: 'access.units' },
      },
      {
        name: 'unit-create',
        path: 'units/create',
        component: () => import('../pages/units/UnitCreate.vue'),
        meta: { permission: 'create.units' },
      },
      {
        name: 'unit-edit',
        path: 'units/:id/edit',
        component: () => import('../pages/units/UnitEdit.vue'),
        meta: { permission: 'access.units' },
      },
      {
        name: 'taxes',
        path: 'taxes',
        component: () => import('../pages/taxes/TaxesPage.vue'),
        meta: { permission: 'access.taxes' },
      },
      {
        name: 'tax-create',
        path: 'taxes/create',
        component: () => import('../pages/taxes/TaxCreate.vue'),
        meta: { permission: 'create.taxes' },
      },
      {
        name: 'tax-edit',
        path: 'taxes/:id/edit',
        component: () => import('../pages/taxes/TaxEdit.vue'),
        meta: { permission: 'update.taxes' },
      },
      {
        name: 'categories',
        path: 'categories',
        component: () => import('../pages/categories/CategoriesPage.vue'),
        meta: { permission: 'access.categories' },
      },
      {
        name: 'category-create',
        path: 'categories/create',
        component: () => import('../pages/categories/CategoryCreate.vue'),
        meta: { permission: 'create.categories' },
      },
      {
        name: 'category-edit',
        path: 'categories/:id/edit',
        component: () => import('../pages/categories/CategoryEdit.vue'),
        meta: { permission: 'access.categories' },
      },
      {
        name: 'products',
        path: 'products',
        component: () => import('../pages/products/ProductsPage.vue'),
        meta: { permission: 'access.products' },
      },
      {
        name: 'product-create',
        path: 'products/create',
        component: () => import('../pages/products/ProductCreate.vue'),
        meta: { permission: 'create.products' },
      },
      {
        name: 'product-edit',
        path: 'products/:id/edit',
        component: () => import('../pages/products/ProductEdit.vue'),
        meta: { permission: 'update.products' },
      },
      {
        name: 'services',
        path: 'services',
        component: () => import('../pages/services/ServicesPage.vue'),
        meta: { permission: 'access.services' },
      },
      {
        name: 'service-create',
        path: 'services/create',
        component: () => import('../pages/services/ServiceCreate.vue'),
        meta: { permission: 'create.services' },
      },
      {
        name: 'service-edit',
        path: 'services/:id/edit',
        component: () => import('../pages/services/ServiceEdit.vue'),
        meta: { permission: 'update.services' },
      },
      {
        name: 'purchases',
        path: 'purchases',
        component: () => import('../pages/purchases/PurchasesPage.vue'),
        meta: { permission: 'access.purchases' },
      },
      {
        name: 'purchase-create',
        path: 'purchases/create',
        component: () => import('../pages/purchases/PurchaseCreate.vue'),
        meta: { permission: 'create.purchases' },
      },
      {
        name: 'purchase-edit',
        path: 'purchases/:id/edit',
        component: () => import('../pages/purchases/PurchaseEdit.vue'),
        meta: { permission: 'update.purchases' },
      },
      {
        name: 'sales',
        path: 'sales',
        component: () => import('../pages/sales/SalesPage.vue'),
        meta: { permission: 'access.sales' },
      },
      {
        name: 'sale-create',
        path: 'sales/create',
        component: () => import('../pages/sales/SaleCreate.vue'),
        meta: { permission: 'create.sales' },
      },
      {
        name: 'sale-edit',
        path: 'sales/:id/edit',
        component: () => import('../pages/sales/SaleEdit.vue'),
        meta: { permission: 'update.sales' },
      },
      {
        name: 'inventory-movements',
        path: 'inventory-movements',
        component: () => import('../pages/inventory-movements/InventoryMovementsPage.vue'),
        meta: { permission: 'access.inventory_movements' },
      },
      {
        name: 'inventory-movement-create',
        path: 'inventory-movements/create',
        component: () => import('../pages/inventory-movements/InventoryMovementCreate.vue'),
        meta: { permission: 'create.inventory_movements' },
      },
      {
        name: 'inventory-movement-view',
        path: 'inventory-movements/:id',
        component: () => import('../pages/inventory-movements/InventoryMovementView.vue'),
        meta: { permission: 'view.inventory_movements' },
      },
      {
        name: 'roles',
        path: 'roles',
        component: () => import('../pages/roles/RolesPage.vue'),
        meta: { permission: 'access.roles' },
      },
      {
        name: 'role-create',
        path: 'roles/create',
        component: () => import('../pages/roles/RoleCreate.vue'),
        meta: { permission: 'create.roles' },
      },
      {
        name: 'role-edit',
        path: 'roles/:id/edit',
        component: () => import('../pages/roles/RoleEdit.vue'),
        meta: { permission: 'update.roles' },
      },
      {
        name: 'permissions',
        path: 'permissions',
        component: () => import('../pages/permissions/PermissionsPage.vue'),
        meta: { permission: 'access.permissions' },
      },
      {
        name: 'permission-create',
        path: 'permissions/create',
        component: () => import('../pages/permissions/PermissionCreate.vue'),
        meta: { permission: 'create.permissions' },
      },
      {
        name: 'permission-edit',
        path: 'permissions/:id/edit',
        component: () => import('../pages/permissions/PermissionEdit.vue'),
        meta: { permission: 'update.permissions' },
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
        meta: { permission: 'access.projects' },
      },
      // Módulo Óptico - Pathologies
      {
        name: 'pathologies',
        path: 'pathologies',
        component: () => import('../pages/optical/pathologies/PathologiesPage.vue'),
        // meta: { permission: 'access.pathologies' },
      },
      {
        name: 'pathology-create',
        path: 'pathologies/create',
        component: () => import('../pages/optical/pathologies/PathologyCreate.vue'),
        // meta: { permission: 'create.pathologies' },
      },
      {
        name: 'pathology-edit',
        path: 'pathologies/:id/edit',
        component: () => import('../pages/optical/pathologies/PathologyEdit.vue'),
        // meta: { permission: 'update.pathologies' },
      },
      // Módulo Óptico - Appointments
      {
        name: 'appointments',
        path: 'appointments',
        component: () => import('../pages/optical/appointments/AppointmentsPage.vue'),
        // meta: { permission: 'access.appointments' },
      },
      {
        name: 'appointment-create',
        path: 'appointments/create',
        component: () => import('../pages/optical/appointments/AppointmentCreate.vue'),
        // meta: { permission: 'create.appointments' },
      },
      {
        name: 'appointment-edit',
        path: 'appointments/:id/edit',
        component: () => import('../pages/optical/appointments/AppointmentEdit.vue'),
        // meta: { permission: 'update.appointments' },
      },
      // Módulo Óptico - Optical Exams
      {
        name: 'exams',
        path: 'exams',
        component: () => import('../pages/optical/exams/ExamsPage.vue'),
        // meta: { permission: 'access.optical_exams' },
      },
      {
        name: 'exam-create',
        path: 'exams/create',
        component: () => import('../pages/optical/exams/ExamCreate.vue'),
        // meta: { permission: 'create.optical_exams' },
      },
      {
        name: 'exam-edit',
        path: 'exams/:id/edit',
        component: () => import('../pages/optical/exams/ExamEdit.vue'),
        // meta: { permission: 'update.optical_exams' },
      },

      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: '403',
    path: '/403',
    component: () => import('../pages/403.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

/**
 * Guard de navegación global
 * Protege rutas que requieren autenticación y verifica permisos
 */
router.beforeEach((to, from, next) => {
  const isAuthenticated = !!tokenService.getAccessToken()
  const isAuthRoute = to.matched.some((record) => record.path.startsWith('/auth'))

  // Si la ruta requiere autenticación y el usuario no está autenticado
  if (!isAuthRoute && !isAuthenticated) {
    // Redirigir al login
    next({ name: 'login' })
    return
  }

  // Si el usuario está autenticado e intenta acceder a rutas de auth
  if (isAuthRoute && isAuthenticated) {
    // Redirigir al módulo de citas
    next({ name: 'appointments' })
    return
  }

  // Verificar permisos si la ruta lo requiere
  if (to.meta.permission && isAuthenticated) {
    const authStore = useAuthStore()

    // Cargar permisos desde localStorage si no están en el store
    if (authStore.permissions.length === 0) {
      authStore.loadPermissionsFromStorage()
    }

    const permission = to.meta.permission as string

    if (!authStore.hasPermission(permission)) {
      // Redirigir a página de acceso denegado
      next({ name: '403' })
      return
    }
  }

  // En cualquier otro caso, permitir la navegación
  next()
})

export default router
